import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Activity, Target, Zap, Trophy, BarChart3, Shield, Crosshair, X, ChevronRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map overview stat labels to ranking endpoint params
// type: "standard" uses /stats/rankings/all-teams/{group}/{key}
// type: "split" uses /stats/rankings/splits/{split_type}
const RANKING_MAP = {
  "RECORD": { type: "split", split: "record", label: "RECORD", description: "Win-Loss Record (by Win %)", format: "record" },
  "DIVISION": { type: "split", split: "al_west", label: "AL WEST", description: "American League West Standings", format: "record" },
  "TEAM OPS": { type: "standard", group: "hitting", key: "ops", label: "OPS", description: "On-Base + Slugging", format: "avg" },
  "TEAM AVG": { type: "standard", group: "hitting", key: "avg", label: "AVG", description: "Team Batting Average", format: "avg" },
  "TEAM ERA": { type: "standard", group: "pitching", key: "era", label: "ERA", description: "Earned Run Average", format: "era" },
  "HOME RUNS": { type: "standard", group: "hitting", key: "home_runs", label: "HR", description: "Home Runs", format: "int" },
  "AVG w/RISP": { type: "split", split: "risp_avg", label: "AVG w/RISP", description: "Batting Average with Runners in Scoring Position", format: "avg" },
  "STARTER ERA": { type: "split", split: "starter_era", label: "STARTER ERA", description: "Starting Pitcher ERA", format: "era" },
  "BULLPEN ERA": { type: "split", split: "bullpen_era", label: "BULLPEN ERA", description: "Relief Pitcher ERA", format: "era" },
};

function DrilldownModal({ stat, onClose }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        let res;
        if (stat.type === "split") {
          res = await axios.get(`${API}/stats/rankings/splits/${stat.split}`);
        } else {
          res = await axios.get(`${API}/stats/rankings/all-teams/${stat.group}/${stat.key}`);
        }
        setTeams(res.data.teams || []);
      } catch (e) {
        console.error("Error fetching ranking:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [stat]);

  const formatValue = (team) => {
    if (team.display_value) return team.display_value;
    const value = team.value;
    if (value === null || value === undefined) return "-";
    if (stat.format === "avg") return value.toFixed(3);
    if (stat.format === "era") return value.toFixed(2);
    if (stat.format === "record") return value.toFixed(3);
    return Math.round(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="overview-drilldown-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-astros-surface border border-astros-border w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-astros-border">
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wide">{stat.label} - ALL MLB TEAMS</h3>
            <p className="text-xs text-astros-muted">{stat.description}</p>
          </div>
          <button onClick={onClose} className="text-astros-muted hover:text-white transition-colors" data-testid="overview-drilldown-close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-1">
              {teams.map((team, idx) => (
                <div
                  key={team.team_id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    team.is_astros
                      ? "bg-astros-orange/15 border border-astros-orange/40"
                      : idx % 2 === 0 ? "bg-astros-border/5" : ""
                  }`}
                >
                  <span className={`w-7 text-center font-bebas text-base ${
                    team.rank <= 3 ? "text-astros-orange" : "text-astros-muted"
                  }`}>
                    {team.rank}
                  </span>
                  <span className={`flex-1 font-ibm-plex text-sm ${team.is_astros ? "text-astros-orange font-bold" : "text-white"}`}>
                    {team.name}
                  </span>
                  <span className="text-xs text-astros-muted w-8 text-center">{team.abbreviation}</span>
                  <span className={`font-mono text-sm font-semibold min-w-[60px] text-right ${
                    team.is_astros ? "text-astros-orange" : "text-white"
                  }`}>
                    {formatValue(team)}
                  </span>
                  {team.games_back !== undefined && (
                    <span className="text-xs text-astros-muted min-w-[40px] text-right">
                      {team.games_back === "—" ? "—" : `${team.games_back} GB`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function TeamStatsOverview({ stats }) {
  const [drilldown, setDrilldown] = useState(null);

  if (!stats) return null;

  const statCards = [
    { label: "RECORD", value: `${stats.wins}-${stats.losses}`, icon: Trophy, color: "text-astros-orange" },
    { label: "DIVISION", value: `${stats.division_rank}${getOrdinalSuffix(stats.division_rank)} in ${stats.division_name?.replace("American League ", "AL ")}`, icon: BarChart3, color: "text-astros-blue" },
    { label: "TEAM OPS", value: stats.team_ops?.toFixed(3) || ".000", icon: TrendingUp, color: "text-astros-success" },
    { label: "TEAM AVG", value: stats.team_avg?.toFixed(3) || ".000", icon: Target, color: "text-white" },
    { label: "TEAM ERA", value: stats.team_era?.toFixed(2) || "0.00", icon: Activity, color: "text-astros-blue" },
    { label: "HOME RUNS", value: stats.home_runs || 0, icon: Zap, color: "text-astros-orange" },
  ];

  const extraCards = [
    { label: "AVG w/RISP", value: stats.avg_risp || ".000", icon: Crosshair, color: "text-astros-success" },
    { label: "STARTER ERA", value: stats.starter_era?.toFixed(2) || "0.00", icon: Activity, color: "text-white" },
    { label: "BULLPEN ERA", value: stats.bullpen_era?.toFixed(2) || "0.00", icon: Shield, color: "text-astros-blue" },
  ];

  const handleCardClick = (label) => {
    const mapping = RANKING_MAP[label];
    if (mapping) {
      setDrilldown(mapping);
    }
  };

  return (
    <section data-testid="team-stats-overview">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-4">
        TEAM OVERVIEW
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-astros-surface border border-astros-border p-4 hover:border-astros-orange transition-colors cursor-pointer group"
            onClick={() => handleCardClick(stat.label)}
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
              <span className="text-xs tracking-widest text-astros-muted font-bold">
                {stat.label}
              </span>
              <ChevronRight size={12} className="text-astros-muted opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
            </div>
            <p className={`font-bebas text-3xl sm:text-4xl tracking-tight ${stat.color}`}>
              {stat.value}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {extraCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (index + 6) * 0.05 }}
            className="bg-astros-surface border border-astros-border p-4 hover:border-astros-orange transition-colors cursor-pointer group"
            onClick={() => handleCardClick(stat.label)}
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
              <span className="text-xs tracking-widest text-astros-muted font-bold">
                {stat.label}
              </span>
              <ChevronRight size={12} className="text-astros-muted opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
            </div>
            <p className={`font-bebas text-3xl sm:text-4xl tracking-tight ${stat.color}`}>
              {stat.value}
            </p>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {drilldown && (
          <DrilldownModal stat={drilldown} onClose={() => setDrilldown(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}

function getOrdinalSuffix(rank) {
  if (!rank) return "";
  const num = parseInt(rank);
  if (isNaN(num)) return "";
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return "";
  if (j === 2 && k !== 12) return "";
  if (j === 3 && k !== 13) return "";
  return "";
}
